import { createSlice } from '@reduxjs/toolkit';
import { setupChatUser, getPendingRequests } from './chatAPI';
import {
  sort,
  composeConversation,
  capitalizeKeys,
  composeHistory,
  getLanguageWord,
} from '../../app/common/libs/helper';
import { LOGOUT } from '../../app/actionTypes';
import {
  ChatStatusMapping,
  ConversationStatus,
  DeliveryReceiptsEnum,
} from '../../app/common/constants/values';
import { toast } from 'react-toastify';
import { uniqBy } from 'lodash';
const initialState = {
  isLoading: false,
  connectionStatus: '',
  chatUserId: '',
  shortKeys: {},
  dispositions: [],
  agents: [],
  selectedChatIndex: null,
  historySelectedChatIndex: null,
  selectChatSid: null,
  isViewHistoryOpen: false,
  webChatHistory: [],
  conversations: {
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    pageNo: 1,
    pageSize: 50,
    total: 0,
    searchValue: '',
    channelType: 4,
  },
  history: {
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    historyMessages: [],
    pageNo: 1,
    pageSize: 50,
    total: 0,
  },
  connectionStatus: 'disconnected',
  requests: [],
  rating: {},
  currentActivity: ChatStatusMapping.Offline,
  filter: 'active',
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    setChatActivity: (state, action) => {
      state.currentActivity = action.payload;
    },
    clearSelection: (state, action) => {
      state.selectedChatIndex = null;
      state.selectChatSid = null;
    },
    addConversation: (state, action) => {
      const length = state.conversations.items.length;
      if (length == 0) {
        state.conversations.items.push(action.payload);
      } else if (state.conversations.items[length - 1].loadMore) {
        state.conversations.items.splice(length - 2, 0, action.payload);
      } else {
        state.conversations.items.push(action.payload);
      }
      sort(state.conversations.items);
      let selectedChatIndex = state.selectedChatIndex;
      const cindex = state.conversations.items.findIndex(
        (x) => x.sid == state.selectChatSid
      );
      if (cindex > -1) {
        selectedChatIndex = cindex;
      }
      state.selectedChatIndex = selectedChatIndex;
    },
    removeConversation: (state, action) => {
      state.conversations.items.splice(action.payload, 1);
      state.conversations.total = state.conversations.total - 1;
      state.selectedChatIndex =
        action.payload == state.selectedChatIndex
          ? null
          : state.selectedChatIndex;
      state.selectChatSid =
        action.payload == state.selectChatSid ? null : state.selectChatSid;
    },
    setConversationStatus: (state, action) => {
      const { sid, status, index } = action.payload;
      state.selectChatSid =
        state.selectedChatIndex == index ? sid : state.selectChatSid;
      state.conversations.items[index].status = status;
      state.conversations.items[index].sid = sid;
      state.conversations.items[index].hasDisposition = false;
      if (status == ConversationStatus.Closed) {
        state.conversations.items[index].isOnline = false;
      }
    },
    setUnReadCount: (state, action) => {
      const { index, count } = action.payload;
      state.conversations.items[index].unread_messages_count = count;
    },
    setCurrentConversation: (state, action) => {
      const { index, sid } = action.payload;
      state.selectedChatIndex = index;
      state.selectChatSid = sid;
      state.conversations.items[index].unread_messages_count = 0;
    },
    setMessages: (state, action) => {
      state.conversations.items[action.payload.index].messages = {
        ...state.conversations.items[state.selectedChatIndex].messages,
        ...action.payload,
      };
    },
    setConversations: (state, action) => {
      state.conversations = {
        ...state.conversations,
        ...action.payload,
      };
    },
    setClosedBy: (state, action) => {
      const { index, closedBy, dateClosed, disposition, note, hasDisposition } =
        action.payload;
      state.conversations.items[index].closedBy = closedBy;
      state.conversations.items[index].dateClosed = dateClosed;
      state.conversations.items[index].disposition = disposition;
      state.conversations.items[index].note = note;
      state.conversations.items[index].hasDisposition = hasDisposition;
    },
    removeRequestMessage: (state, action) => {
      state.requests.splice(action.payload, 1);
    },
    addRequestMessage: (state, action) => {
      state.requests.push(action.payload);
    },
    updateRequestMessage: (state, action) => {
      const { index, message, messageTime } = action.payload;
      state.requests[index].MessageText = message;
      state.requests[index].MessageDateTime = messageTime;
    },
    setSuccess: (state, action) => {
      //state.success = false;
    },
    setError: (state, action) => {
      //state.error = action.payload;
    },
    setLoader: (state, action) => {
      state.isLoading = action.payload;
    },
    mapConversationState: (state, action) => {
      const { conversations, totalCount } = action.payload;
      const length = state.conversations.items.length;
      if (length > 0) {
        if (state.conversations.items[length - 1].loadMore) {
          state.conversations.items.splice(length - 1, 1);
        }
      }
      const cnv = conversations.map(composeConversation);
      cnv.forEach((c) => {
        const index = state.conversations.items.findIndex(
          (x) => x.uniqueName == c.uniqueName
        );
        if (index == -1) {
          state.conversations.items.push(c);
        } else {
          state.conversations.items[index].id = c.id;
          state.conversations.items[index].sid = c.sid;
        }
      });

      const hasNextPage = state.conversations.items < totalCount;
      if (hasNextPage) {
        state.conversations.items.push({
          id: '',
          sid: '',
          uniqueName: '',
          friendlyName: '',
          loadMore: true,
        });
      }
      state.conversations.hasNextPage = hasNextPage;
      state.conversations.isNextPageLoading = false;
      state.conversations.total = totalCount;
    },
    setTypingInfo: (state, action) => {
      const { index, typing } = action.payload;
      state.conversations.items[index].messages.typing = typing;
    },
    setTypingMessage: (state, action) => {
      const { index, message } = action.payload;
      state.conversations.items[index].messages.typing.message = message;
    },
    addMessage: (state, action) => {
      let count = 0;
      const { index, message } = action.payload;
      if (index != state.selectedChatIndex) {
        count = state.conversations.items[index].unread_messages_count + 1;
      }
      state.conversations.items[index].lastMessage = {
        body: message.body,
        sid: message.sid,
        dateCreated: message.datetime.toISOString(),
      };
      state.conversations.items[index].unread_messages_count = count;
      state.conversations.items[index].messages.items.push(
        action.payload.message
      );
      sort(state.conversations.items);
      let selectedChatIndex = state.selectedChatIndex;
      const cindex = state.conversations.items.findIndex(
        (x) => x.sid == state.selectChatSid
      );
      if (cindex > -1) {
        selectedChatIndex = cindex;
      }
      state.selectedChatIndex = selectedChatIndex;
    },
    editMessage: (state, action) => {
      const { ch_index, msg_index, attributes, lastMessageTime, receipt } =
        action.payload;
      state.conversations.items[ch_index].lastMessage.dateCreated =
        lastMessageTime;
      state.conversations.items[ch_index].messages.items[msg_index].attributes =
        attributes;
      state.conversations.items[ch_index].messages.items[
        msg_index
      ].deliveryReceipt = receipt;
      sort(state.conversations.items);
      let selectedChatIndex = state.selectedChatIndex;
      const index = state.conversations.items.findIndex(
        (x) => x.sid == state.selectChatSid
      );
      if (index > -1) {
        selectedChatIndex = index;
      }
      state.selectedChatIndex = selectedChatIndex;
    },
    setRatingValue: (state, action) => {
      state.rating = action.payload;
      state.isLoading = false;
    },
    setHasRated: (state, action) => {
      const { index, hasRated } = action.payload;
      state.conversations.items[index].hasRated = hasRated;
    },
    setCustomerOnlineStatus: (state, action) => {
      const { sid, online } = action.payload;
      const conv = state.conversations.items.find((x) => x.sid == sid);
      if (conv) {
        conv.isOnline = online;
      }
    },
    clearCurrentConversation: (state, action) => {
      state.selectedChatIndex = null;
      state.selectChatSid = null;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setCustomerInfo: (state, action) => {
      const conv = state.conversations.items[state.selectedChatIndex];
      if (conv && conv.customerInfo) {
        if (action.payload.email) conv.friendlyName = action.payload.email;

        conv.customerInfo.name = action.payload.name;
        conv.customerInfo.email = action.payload.email;
        conv.customerInfo.btn = action.payload.phone;
      }
    },
    setCustomerOnlineStatusTimeout: (state, action) => {
      const { sid, customerOnlineStatusTimeout } = action.payload;
      const conv = state.conversations.items.find((x) => x.sid === sid);
      if (conv) {
        conv.customerOnlineStatusTimeout = customerOnlineStatusTimeout;
      }
    },
    updateMessageDelivered: (state, action) => {
      const { ConversationId, Time, Type } = action.payload;
      const conv = state.conversations.items.find(
        (x) => x.id == ConversationId
      );
      if (conv) {
        const d = DeliveryReceiptsEnum[Type];
        let messages = conv.messages.items.filter(
          (x) => x.datetime > new Date(Time) && x.deliveryReceipt == undefined
        );
        if (messages.length == 0 && Type == 2) {
          messages = conv.messages.items.filter(
            (x) =>
              x.datetime > new Date(Time) &&
              x.deliveryReceipt != undefined &&
              x.deliveryReceipt.delivered == 'all' &&
              x.direction == 'outgoing'
          );
        }
        if (messages.length > 0) {
          messages.forEach((x) => {
            let status = {
              delivered: 'none',
              failed: 'none',
              read: 'none',
              sent: 'none',
              undelivered: 'none',
            };
            status[d] = 'all';
            x.deliveryReceipt = status;
          });
        }
      }
    },
    mapWebChatHistory: (state, action) => {
      if (state.selectedChatIndex === null) return;

      const { webChatHistory } = action.payload;

      let history = webChatHistory.map(composeHistory);

      const visitorId =
        state.conversations.items[state.selectedChatIndex].visitorId;
      const historyByVisitorId = state.history.items.filter(
        (x) => x.visitorId == visitorId
      );
      if (historyByVisitorId.length > 0) {
        for (const item of historyByVisitorId) {
          history = history.filter(
            (x) => x.conversationId != item.conversationId
          );
        }
      }

      state.conversations.items[state.selectedChatIndex].webChatHistory =
        history;
      state.history.items = [...state.history.items, ...history];
    },
    setViewHistoryOpen: (state, action) => {
      state.isViewHistoryOpen = action.payload;
    },
    setMessageHistory: (state, action) => {
      const visitorId = state.conversations.items[state.selectedChatIndex].visitorId;
      const historyByVisitorId = state.history.items.filter(x=> x.visitorId == visitorId);
      historyByVisitorId[state.historySelectedChatIndex].messages = {...historyByVisitorId[state.historySelectedChatIndex].messages, ...action.payload};
    },
    setHistorySelectedIndex: (state, action) => {
      state.historySelectedChatIndex = action.payload;
    },
    clearHistory: (state, action) => {
      // state.history = {
      //   hasNextPage: true,
      //   isNextPageLoading: false,
      //   items: [],
      //   historyMessages: [],
      //   pageNo: 1,
      //   pageSize: 50,
      //   total: 0
      // };
      state.history.historyMessages = [];
      //state.historySelectedChatIndex = null;
      state.isViewHistoryOpen = false;
    },
    setHistoryMessages: (state, action) => {
      const visitorId =
        state.conversations.items[state.selectedChatIndex].visitorId;
      const historyByVisitorId = state.history.items.filter(
        (x) => x.visitorId == visitorId
      );
      var index = historyByVisitorId.findIndex(
        (x) => x.conversationId == action.payload
      );
      var messages = historyByVisitorId[index].messages.items;
        state.history.historyMessages = uniqBy([...messages, ...state.history.historyMessages], "sid");
    },
    setDispositionShortCuts: (state, action) => {
      const { userId, dispositions, chatShortKeys } = action.payload;
      state.chatUserId = userId;
      state.dispositions = dispositions;
      state.shortKeys = chatShortKeys;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOGOUT, (state, action) => {
      return { ...initialState, requests: state.requests };
    });
    builder.addCase(getPendingRequests.rejected, (state, action) => {
      const languageKeys = action.payload.languageKeys;
      console.log(action.payload);
      toast.error(
        getLanguageWord(
          languageKeys.value?.Error_Pending_Request,
          'Error loading pending offline requests'
        )
      );
    });
    builder.addCase(getPendingRequests.fulfilled, (state, action) => {
      const languageKeys = action.payload.languageKeys;
      const data = action.payload.response.data;
      if (data.length > 0) {
        toast.info(
          `${getLanguageWord(languageKeys.value?.You_Have, 'You have')} ${
            data.length
          } ${getLanguageWord(
            languageKeys.value?.Offline_Pending_Request,
            'offline pending requests'
          )}`
        );
      }
      for (let i = 0; i < data.length; i++) {
        const request = capitalizeKeys(data[i]);
        const r = state.requests.find(
          (y) => y.CustomerConversationSid == request.CustomerConversationSid
        );
        if (!r) {
          state.requests.push(request);
        }
      }
    });
  },
});

export const {
  setConnectionStatus,
  setSuccess,
  setError,
  setChatActivity,
  clearSelection,
  addConversation,
  removeConversation,
  setConversationStatus,
  setLoader,
  setUnReadCount,
  setClosedBy,
  setCurrentConversation,
  setMessages,
  setConversations,
  removeRequestMessage,
  addRequestMessage,
  updateRequestMessage,
  mapConversationState,
  setTypingInfo,
  setTypingMessage,
  addMessage,
  editMessage,
  setRatingValue,
  setHasRated,
  setCustomerOnlineStatus,
  clearCurrentConversation,
  setFilter,
  setCustomerInfo,
  setCustomerOnlineStatusTimeout,
  updateMessageDelivered,
  mapWebChatHistory,
  setViewHistoryOpen,
  setMessageHistory,
  setHistorySelectedIndex,
  clearHistory,
  setHistoryMessages,
  setDispositionShortCuts,
} = chatSlice.actions;

export default chatSlice.reducer;
